import React, { useEffect, useMemo, useState } from "react"
import { Loader2, ChevronRight } from "lucide-react"
import { Button } from "@/components/ui/button"
import { useAdminStore, type AdminUsageRow } from "@/stores/admin-store"
import { cn } from "@/lib/utils"

const DAY_MS = 24 * 60 * 60 * 1000
const DATE_INPUT_CLASS = "bg-muted rounded-lg text-sm px-2 h-9 border border-border"

function utcDayStart(d: Date): number {
  return Date.UTC(d.getUTCFullYear(), d.getUTCMonth(), d.getUTCDate())
}

function formatCost(value: number): string {
  return `$${value.toFixed(4)}`
}

function formatInt(value: number): string {
  return value.toLocaleString("en-AU")
}

/** Tokens / pages / queries — whichever units this event type actually meters. */
function formatUnits(
  row: Pick<AdminUsageRow, "inputTokens" | "outputTokens" | "pages" | "quantity">
): string {
  const parts: string[] = []
  if (row.inputTokens || row.outputTokens) {
    parts.push(`${formatInt(row.inputTokens)} in / ${formatInt(row.outputTokens)} out tokens`)
  }
  if (row.pages) parts.push(`${formatInt(row.pages)} pages`)
  if (row.quantity) parts.push(`${formatInt(row.quantity)} queries`)
  return parts.length ? parts.join(" · ") : "—"
}

function sumCost(rows: AdminUsageRow[], orgId: string, sinceMs: number): number {
  return rows
    .filter((r) => r.organizationId === orgId && new Date(r.day).getTime() >= sinceMs)
    .reduce((acc, r) => acc + r.costUsd, 0)
}

interface BreakdownRow {
  key: string
  feature: string
  eventType: string
  costUsd: number
  inputTokens: number
  outputTokens: number
  pages: number
  quantity: number
  eventCount: number
}

function buildBreakdown(rows: AdminUsageRow[], orgId: string, sinceMs: number): BreakdownRow[] {
  const byKey = new Map<string, BreakdownRow>()
  for (const row of rows) {
    if (row.organizationId !== orgId) continue
    if (new Date(row.day).getTime() < sinceMs) continue
    const feature = row.feature ?? "—"
    const key = `${feature}::${row.eventType}`
    const existing = byKey.get(key)
    if (existing) {
      existing.costUsd += row.costUsd
      existing.inputTokens += row.inputTokens
      existing.outputTokens += row.outputTokens
      existing.pages += row.pages
      existing.quantity += row.quantity
      existing.eventCount += row.eventCount
    } else {
      byKey.set(key, {
        key,
        feature,
        eventType: row.eventType,
        costUsd: row.costUsd,
        inputTokens: row.inputTokens,
        outputTokens: row.outputTokens,
        pages: row.pages,
        quantity: row.quantity,
        eventCount: row.eventCount,
      })
    }
  }
  return [...byKey.values()].sort((a, b) => b.costUsd - a.costUsd)
}

export function AdminUsagePage() {
  const { usageRows, usageOrgs, isUsageLoading, loadUsage, error } = useAdminStore()
  const [expandedOrgId, setExpandedOrgId] = useState<string | null>(null)
  const [fromInput, setFromInput] = useState("")
  const [toInput, setToInput] = useState("")
  const [isCustomRange, setIsCustomRange] = useState(false)

  useEffect(() => {
    void loadUsage()
  }, [loadUsage])

  const todayStart = utcDayStart(new Date())
  // A custom range is already narrowed server-side, so every fetched row counts.
  const breakdownSince = isCustomRange ? -Infinity : todayStart - 29 * DAY_MS

  const orgTotals = useMemo(() => {
    const orgIds = [...new Set(usageRows.map((r) => r.organizationId))]
    return orgIds
      .map((id) => ({
        id,
        name: usageOrgs.find((o) => o.id === id)?.name ?? "Deleted organisation",
        today: sumCost(usageRows, id, todayStart),
        last7: sumCost(usageRows, id, todayStart - 6 * DAY_MS),
        last30: sumCost(usageRows, id, todayStart - 29 * DAY_MS),
        range: sumCost(usageRows, id, -Infinity),
      }))
      .sort((a, b) => (isCustomRange ? b.range - a.range : b.last30 - a.last30))
  }, [usageRows, usageOrgs, todayStart, isCustomRange])

  const totals = orgTotals.reduce(
    (acc, o) => ({
      today: acc.today + o.today,
      last7: acc.last7 + o.last7,
      last30: acc.last30 + o.last30,
      range: acc.range + o.range,
    }),
    { today: 0, last7: 0, last30: 0, range: 0 }
  )

  const handleApply = () => {
    if (!fromInput || !toInput) return
    setIsCustomRange(true)
    setExpandedOrgId(null)
    void loadUsage(fromInput, toInput)
  }

  const handleReset = () => {
    setFromInput("")
    setToInput("")
    setIsCustomRange(false)
    setExpandedOrgId(null)
    void loadUsage()
  }

  const costColumns: Array<{ key: "today" | "last7" | "last30" | "range"; label: string; emphasis: boolean }> =
    isCustomRange
      ? [{ key: "range", label: "Range cost", emphasis: true }]
      : [
          { key: "today", label: "Today", emphasis: false },
          { key: "last7", label: "7 days", emphasis: false },
          { key: "last30", label: "30 days", emphasis: true },
        ]

  return (
    <div className="flex flex-col h-full">
      <div className="flex-1 overflow-auto px-6 py-4">
        <div className="flex items-end justify-between gap-4 mb-4">
          <p className="text-meta max-w-md">
            All figures UTC. LLM, Textract, embedding and rerank costs; fixed infrastructure
            excluded.
          </p>
          <div className="flex items-center gap-2">
            <input
              type="date"
              aria-label="From"
              value={fromInput}
              onChange={(e) => setFromInput(e.target.value)}
              className={DATE_INPUT_CLASS}
            />
            <span className="text-meta">to</span>
            <input
              type="date"
              aria-label="To"
              value={toInput}
              onChange={(e) => setToInput(e.target.value)}
              className={DATE_INPUT_CLASS}
            />
            <Button variant="ghost" size="sm" disabled={!fromInput || !toInput} onClick={handleApply}>
              Apply
            </Button>
            {isCustomRange && (
              <Button variant="ghost" size="sm" onClick={handleReset}>
                Reset
              </Button>
            )}
          </div>
        </div>

        {error && <p className="text-sm text-destructive mb-3">{error}</p>}

        {isUsageLoading ? (
          <div className="flex items-center justify-center h-32">
            <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
          </div>
        ) : orgTotals.length === 0 ? (
          <p className="text-sm text-muted-foreground py-8 text-center">
            No usage recorded for this period.
          </p>
        ) : (
          <div className="rounded-lg border bg-card shadow-sm overflow-hidden">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b bg-muted/50">
                  <th className="text-left px-3 py-2 font-medium text-muted-foreground">
                    Organisation
                  </th>
                  {costColumns.map((col) => (
                    <th
                      key={col.key}
                      className="text-right px-3 py-2 font-medium text-muted-foreground"
                    >
                      {col.label}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y">
                {orgTotals.map((org) => {
                  const isExpanded = expandedOrgId === org.id
                  const breakdown = isExpanded
                    ? buildBreakdown(usageRows, org.id, breakdownSince)
                    : []

                  return (
                    <React.Fragment key={org.id}>
                      <tr
                        className={cn(
                          "hover:bg-muted/40 cursor-pointer",
                          isExpanded && "bg-muted/20"
                        )}
                        onClick={() => setExpandedOrgId(isExpanded ? null : org.id)}
                      >
                        <td className="px-3 py-2 font-medium">
                          <div className="flex items-center gap-1.5">
                            <ChevronRight
                              className={cn(
                                "h-3.5 w-3.5 text-muted-foreground transition-transform",
                                isExpanded && "rotate-90"
                              )}
                            />
                            {org.name}
                          </div>
                        </td>
                        {costColumns.map((col) => (
                          <td
                            key={col.key}
                            className={cn(
                              "px-3 py-2 text-right tabular-nums",
                              !col.emphasis && "text-muted-foreground"
                            )}
                          >
                            {formatCost(org[col.key])}
                          </td>
                        ))}
                      </tr>
                      {isExpanded && (
                        <tr>
                          <td
                            colSpan={costColumns.length + 1}
                            className="bg-muted/10 px-3 py-3"
                          >
                            {breakdown.length === 0 ? (
                              <p className="text-meta py-2 pl-5">
                                No usage in this window.
                              </p>
                            ) : (
                              <table className="w-full text-xs">
                                <thead>
                                  <tr className="text-muted-foreground">
                                    <th className="text-left px-2 py-1 pl-5 font-medium">
                                      Feature
                                    </th>
                                    <th className="text-left px-2 py-1 font-medium">Event type</th>
                                    <th className="text-left px-2 py-1 font-medium">Units</th>
                                    <th className="text-right px-2 py-1 font-medium">Calls</th>
                                    <th className="text-right px-2 py-1 font-medium">Cost</th>
                                  </tr>
                                </thead>
                                <tbody>
                                  {breakdown.map((row) => (
                                    <tr key={row.key} className="border-t border-border/50">
                                      <td className="px-2 py-1.5 pl-5">{row.feature}</td>
                                      <td className="px-2 py-1.5 text-muted-foreground">
                                        {row.eventType}
                                      </td>
                                      <td className="px-2 py-1.5 text-muted-foreground">
                                        {formatUnits(row)}
                                      </td>
                                      <td className="px-2 py-1.5 text-right tabular-nums text-muted-foreground">
                                        {formatInt(row.eventCount)}
                                      </td>
                                      <td className="px-2 py-1.5 text-right tabular-nums">
                                        {formatCost(row.costUsd)}
                                      </td>
                                    </tr>
                                  ))}
                                </tbody>
                              </table>
                            )}
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  )
                })}
                <tr className="bg-muted/30">
                  <td className="px-3 py-2 font-medium">Total</td>
                  {costColumns.map((col) => (
                    <td
                      key={col.key}
                      className="px-3 py-2 text-right font-medium tabular-nums"
                    >
                      {formatCost(totals[col.key])}
                    </td>
                  ))}
                </tr>
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  )
}
